import { IMediaItem } from 'types/media'
import { IAudioItem } from 'types/audio'
import { TActiveTimelineItemType } from 'store/types/editor'
import { PROJECT_AI_MEDIA_TYPES, PROJECT_MEDIA_TYPES } from 'consts'
import { ITimelineItem } from 'types/timeline'
import { ItemClickHandlerType } from './types'

export const getVisualMediaTracks = (
  mediaItems: IMediaItem[]
): IMediaItem[][] => {
  const groupedMediaItems: Record<number, IMediaItem[]> = {}

  for (const mediaItem of mediaItems) {
    if (!groupedMediaItems[mediaItem.zIndex]) {
      groupedMediaItems[mediaItem.zIndex] = []
    }
    groupedMediaItems[mediaItem.zIndex].push(mediaItem)
  }

  return Object.keys(groupedMediaItems)
    .map(Number)
    .sort((a, b) => b - a)
    .map(zIndex => groupedMediaItems[zIndex])
}

export const getAudioTracks = (audioItems: IAudioItem[]): IAudioItem[][] => {
  const groupedAudioItems: IAudioItem[][] = []

  for (const audioItem of audioItems) {
    const index = audioItem.lineIndex ?? 0
    if (!groupedAudioItems[index]) {
      groupedAudioItems[index] = []
    }
    groupedAudioItems[index].push(audioItem)
  }

  return groupedAudioItems
}

export const getIsAiMediaItemType = (type: TActiveTimelineItemType) =>
  !!PROJECT_AI_MEDIA_TYPES[type]

export const getIsMediaItemType = (type: TActiveTimelineItemType) =>
  !!PROJECT_MEDIA_TYPES[type]

// Helper function to get visual track index for proper separation
const getVisualTrackIndex = (item: ITimelineItem): number => {
  const isMediaItem = getIsMediaItemType(item.type)

  if (isMediaItem) {
    // For media items, use zIndex as the track index
    // Media items have zIndex property, cast to include it
    const zIndex = (item as ITimelineItem & { zIndex?: number }).zIndex ?? 0
    return zIndex
  } else {
    // For audio items, offset by 1000 to avoid conflicts with media zIndex
    // This ensures audio tracks are separate from media tracks
    return 1000 + item.lineIndex
  }
}

export const getItemsInRange = (
  timelineItems: ITimelineItem[],
  anchorId: string,
  targetId: string
) => {
  const anchorItem = timelineItems.find(item => item.id === anchorId)
  const targetItem = timelineItems.find(item => item.id === targetId)

  if (!anchorItem || !targetItem) {
    return []
  }

  const anchorStartTime = anchorItem.startTime
  const targetStartTime = targetItem.startTime
  const minStartTime = Math.min(anchorStartTime, targetStartTime)
  const maxStartTime = Math.max(anchorStartTime, targetStartTime)

  const anchorVisualTrack = getVisualTrackIndex(anchorItem)
  const targetVisualTrack = getVisualTrackIndex(targetItem)
  const minVisualTrack = Math.min(anchorVisualTrack, targetVisualTrack)
  const maxVisualTrack = Math.max(anchorVisualTrack, targetVisualTrack)

  // Select items within both time range AND visual track range (proper rectangular selection)
  return timelineItems.filter(item => {
    const itemVisualTrack = getVisualTrackIndex(item)
    return (
      item.startTime >= minStartTime &&
      item.startTime <= maxStartTime &&
      itemVisualTrack >= minVisualTrack &&
      itemVisualTrack <= maxVisualTrack
    )
  })
}

export const getHandlerType = (
  event?: React.MouseEvent<HTMLDivElement>,
  hasAnchor?: boolean
) => {
  const isMultiSelectModifier = event?.metaKey || event?.ctrlKey
  const isRangeSelectModifier = event?.shiftKey

  if (isRangeSelectModifier && hasAnchor) {
    return ItemClickHandlerType.RANGE_SELECT
  }

  if (isMultiSelectModifier) {
    return ItemClickHandlerType.MULTI_SELECT
  }

  return ItemClickHandlerType.DEFAULT
}
